import React, { useState, useEffect } from 'react';
import { intakeAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion';
import {
  Search, FileText, Eye, ClipboardList, CheckCircle2, Clock, AlertTriangle,
  User, Phone, Mail, Calendar, MapPin, Image as ImageIcon, Download, X, Loader2,
  Pill, Stethoscope, ShieldCheck, Contact, FileSignature,
} from 'lucide-react';

const PFBadge = ({ forward }) => {
  const state = forward?.state;
  if (state === 'success') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Sent to Practice Fusion</span>;
  }
  if (state === 'failed') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"><Clock className="h-3.5 w-3.5" /> Pending</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-400">—</span>;
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-sm font-medium text-slate-800 break-words">{value || '—'}</p>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{children}</div>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const PHOTO_LABELS = {
  insurance_front: 'Insurance Card (Front)',
  insurance_back: 'Insurance Card (Back)',
  id_photo: 'Photo ID',
  signature: 'Signature',
};

const PatientIntakeSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [photo, setPhoto] = useState(null); // { slug, label, url, loading }

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const res = await intakeAPI.getSubmissions({ limit: 20 });
      setSubmissions(res.data.submissions || []);
    } catch (error) {
      // silent; show empty state
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (s) => {
    setSelected(s);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await intakeAPI.getSubmission(s.id);
      setDetail(res.data);
    } catch (error) {
      setDetail(s); // fall back to row data
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
  };

  const openPhoto = async (slug) => {
    if (!selected) return;
    setPhoto({ slug, label: PHOTO_LABELS[slug], url: null, loading: true });
    try {
      const res = await intakeAPI.getPhoto(selected.id, slug);
      const url = URL.createObjectURL(res.data);
      setPhoto({ slug, label: PHOTO_LABELS[slug], url, loading: false });
    } catch (error) {
      setPhoto({ slug, label: PHOTO_LABELS[slug], url: null, loading: false, error: true });
    }
  };

  const closePhoto = () => {
    if (photo?.url) URL.revokeObjectURL(photo.url);
    setPhoto(null);
  };

  const filtered = submissions.filter((s) => {
    const q = search.toLowerCase();
    const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
    return name.includes(q) || (s.phone || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
  });

  const d = detail || {};
  const photos = d.photos || {};

  const PhotoTile = ({ slug }) => (
    <button
      type="button"
      onClick={() => openPhoto(slug)}
      className="group flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
      data-testid={`view-photo-${slug}`}
    >
      <ImageIcon className="w-4 h-4 text-teal-600" /> {PHOTO_LABELS[slug]}
      <Eye className="w-3.5 h-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );

  const PhotoRow = ({ slugs, label }) => {
    const present = slugs.filter((s) => photos[s]);
    if (!present.length) return null;
    return (
      <div className="mt-4 border-t border-slate-100 pt-3">
        <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> {label}</p>
        <div className="flex flex-wrap gap-2">{present.map((s) => <PhotoTile key={s} slug={s} />)}</div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="intake-submissions-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-teal-600" /> Patient Intake Submissions
        </h1>
        <p className="text-sm text-slate-500 mt-1">Forms submitted through the native patient intake link</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name, phone or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="intake-search-input"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-2">
              <ClipboardList className="w-10 h-10 text-slate-300" />
              <p>No patient intake submissions yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Date of Birth</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Practice Fusion</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} data-testid={`intake-row-${s.id}`}>
                    <TableCell className="font-medium text-slate-900">{`${s.first_name || ''} ${s.last_name || ''}`.trim() || '—'}</TableCell>
                    <TableCell className="text-slate-600">{s.phone || '—'}</TableCell>
                    <TableCell className="text-slate-600">{s.date_of_birth || '—'}</TableCell>
                    <TableCell className="text-slate-600">{formatDate(s.created_at)}</TableCell>
                    <TableCell data-testid={`intake-pf-${s.id}`}><PFBadge forward={s.forward} /></TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => openDetail(s)}
                        className="inline-flex items-center gap-1.5 text-teal-600 hover:text-teal-700 text-sm font-medium"
                        data-testid={`view-intake-${s.id}`}
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog — basic info up top, everything else collapsed */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
            <DialogTitle className="flex items-center gap-3 flex-wrap">
              <span>{`${selected?.first_name || ''} ${selected?.last_name || ''}`.trim() || 'Patient Intake'}</span>
              <PFBadge forward={selected?.forward} />
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="overflow-y-auto px-6 py-5 space-y-5" data-testid="intake-detail-body">
            {detailLoading && !detail ? (
              <div className="py-16 flex items-center justify-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading submission…
              </div>
            ) : (
              <>
                {/* Basic info — always visible */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="intake-basic-info">
                  <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-3">Patient Overview</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    <div className="flex items-center gap-2 text-sm text-slate-700"><User className="w-4 h-4 text-slate-400 shrink-0" /><span className="font-medium">{`${d.first_name || ''} ${d.last_name || ''}`.trim() || '—'}</span></div>
                    <div className="flex items-center gap-2 text-sm text-slate-700"><Calendar className="w-4 h-4 text-slate-400 shrink-0" />DOB: <span className="font-medium">{d.date_of_birth || '—'}</span>{d.sex ? ` · ${d.sex}` : ''}</div>
                    <div className="flex items-center gap-2 text-sm text-slate-700"><Phone className="w-4 h-4 text-slate-400 shrink-0" /><span className="font-medium">{d.phone || '—'}</span></div>
                    <div className="flex items-center gap-2 text-sm text-slate-700"><Mail className="w-4 h-4 text-slate-400 shrink-0" /><span className="font-medium break-all">{d.email || '—'}</span></div>
                    <div className="flex items-start gap-2 text-sm text-slate-700 sm:col-span-2"><MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /><span className="font-medium">{[d.address_street, d.address_city, d.address_state, d.address_zip].filter(Boolean).join(', ') || '—'}</span></div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                    <span>Clinic: <span className="font-medium text-slate-700">{d.clinic_code || '—'}</span></span>
                    <span>Submitted: <span className="font-medium text-slate-700">{formatDate(d.created_at)}</span></span>
                    <span>Status: <span className="font-medium text-slate-700">{d.status || '—'}</span></span>
                  </div>
                </div>

                {/* Collapsed sections — photos live under their own category */}
                <Accordion type="multiple" className="space-y-2.5">
                  <AccordionItem value="pharmacy" className="border border-slate-200 rounded-xl px-4 bg-white shadow-sm data-[state=open]:border-teal-200 data-[state=open]:bg-teal-50/30">
                    <AccordionTrigger className="text-slate-800 font-semibold hover:no-underline" data-testid="section-pharmacy">
                      <span className="flex items-center gap-2"><Pill className="w-4 h-4 text-teal-600" /> Pharmacy</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      {d.pharmacy_pf_verified === false && (
                        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="pharmacy-pf-warning">
                          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                          <span><strong>Pharmacy not found in Practice Fusion.</strong> This address did not match a pharmacy in PF — please verify it before sending prescriptions.</span>
                        </div>
                      )}
                      {d.pharmacy_pf_verified === true && (
                        <div className="mb-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="pharmacy-pf-verified">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                          <span>Verified in Practice Fusion{d.pharmacy_pf_name ? ` — ${d.pharmacy_pf_name}` : ''}</span>
                        </div>
                      )}
                      <Grid>
                        <Field label="Pharmacy Name" value={d.pharmacy_name} />
                        <Field label="Phone" value={d.pharmacy_phone} />
                        <Field label="Address" value={[d.pharmacy_street, d.pharmacy_city, d.pharmacy_state, d.pharmacy_zip].filter(Boolean).join(', ')} />
                      </Grid>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="medical" className="border border-slate-200 rounded-xl px-4 bg-white shadow-sm data-[state=open]:border-teal-200 data-[state=open]:bg-teal-50/30">
                    <AccordionTrigger className="text-slate-800 font-semibold hover:no-underline" data-testid="section-medical">
                      <span className="flex items-center gap-2"><Stethoscope className="w-4 h-4 text-teal-600" /> Medical Information</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Grid>
                        <Field label="Drug Allergies" value={d.allergies} />
                        <Field label="Other Allergy" value={d.other_allergy} />
                        <Field label="Other Allergy Detail" value={d.other_allergy_detail} />
                        <Field label="Pregnant" value={d.is_pregnant} />
                        <Field label="Taking Medications" value={d.taking_medications} />
                        <Field label="Medications Detail" value={d.medications_detail} />
                        <Field label="How did you hear" value={d.referral_source} />
                      </Grid>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="emergency" className="border border-slate-200 rounded-xl px-4 bg-white shadow-sm data-[state=open]:border-teal-200 data-[state=open]:bg-teal-50/30">
                    <AccordionTrigger className="text-slate-800 font-semibold hover:no-underline" data-testid="section-emergency">
                      <span className="flex items-center gap-2"><Contact className="w-4 h-4 text-teal-600" /> Emergency Contact</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Grid>
                        <Field label="Has Emergency Contact" value={d.has_emergency_contact} />
                        <Field label="Name" value={d.emergency_contact_name} />
                        <Field label="Relationship" value={d.emergency_contact_relationship} />
                        <Field label="Phone" value={d.emergency_contact_phone} />
                      </Grid>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="insurance" className="border border-slate-200 rounded-xl px-4 bg-white shadow-sm data-[state=open]:border-teal-200 data-[state=open]:bg-teal-50/30">
                    <AccordionTrigger className="text-slate-800 font-semibold hover:no-underline" data-testid="section-insurance">
                      <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-600" /> Insurance & Documents</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Grid>
                        <Field label="Has Insurance" value={d.has_insurance} />
                      </Grid>
                      <PhotoRow slugs={['insurance_front', 'insurance_back', 'id_photo']} label="Uploaded documents" />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="consents" className="border border-slate-200 rounded-xl px-4 bg-white shadow-sm data-[state=open]:border-teal-200 data-[state=open]:bg-teal-50/30">
                    <AccordionTrigger className="text-slate-800 font-semibold hover:no-underline" data-testid="section-consents">
                      <span className="flex items-center gap-2"><FileSignature className="w-4 h-4 text-teal-600" /> Consents & Signature</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Grid>
                        <Field label="Financial Name" value={d.financial_name} />
                        <Field label="Financial Date" value={d.financial_date} />
                        <Field label="HIPAA Name" value={d.hipaa_name} />
                        <Field label="Telemedicine Name" value={d.telemedicine_name} />
                        <Field label="Telemedicine DOB" value={d.telemedicine_dob} />
                        <Field label="Telemedicine Date" value={d.telemedicine_date} />
                        <Field label="Signature Name" value={d.signature_name} />
                        <Field label="SMS Consent" value={d.sms_consent ? 'Yes' : 'No'} />
                      </Grid>
                      <PhotoRow slugs={['signature']} label="Signature" />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Photo lightbox */}
      <Dialog open={!!photo} onOpenChange={(o) => !o && closePhoto()}>
        <DialogContent className="max-w-2xl" data-testid="photo-lightbox">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ImageIcon className="w-5 h-5 text-teal-600" /> {photo?.label}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {photo?.loading ? (
              <div className="py-20 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading photo…</div>
            ) : photo?.error ? (
              <div className="py-20 flex flex-col items-center justify-center text-slate-400 gap-2"><X className="w-8 h-8 text-red-400" /> Could not load this photo.</div>
            ) : photo?.url ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                  <img src={photo.url} alt={photo.label} className="max-h-[60vh] w-auto object-contain" data-testid="photo-lightbox-image" />
                </div>
                <div className="flex justify-end">
                  <a href={photo.url} download={`${photo.slug}.png`} className="inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700" data-testid="photo-download-link">
                    <Download className="w-4 h-4" /> Download
                  </a>
                </div>
              </div>
            ) : null}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientIntakeSubmissions;
